import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { resetDatasetSessionFilters } from './grid-view-model';

/**
 * The part of a path that decides whether you are still looking at the same
 * thing: `/{org}/{section}/{id}`.
 *
 * Anything deeper is a view of that thing rather than a different one. The
 * artifact focus view is `/{org}/chats/{chatId}/artifact/{artifactId}` — a
 * deeper path, same chat — which is exactly the case a reader expects their
 * filter to survive.
 */
const scopeKeyOf = (pathname: string): string =>
  pathname.split('/').slice(0, 4).join('/');

/**
 * Drops every dataset's carried filters when the app leaves the thing they were
 * set on.
 *
 * A filter is a question asked of the data in front of you. It should outlive
 * the grid being torn down and rebuilt — opening the focus view, resizing the
 * pane, switching artifacts and coming back — and not outlive leaving for
 * something else. Those are indistinguishable from inside the grid, which sees
 * an unmount either way, so the scope is watched from above instead.
 *
 * Mounted once, for the life of the shell.
 */
export const useDatasetFilterScope = (): void => {
  const { pathname } = useLocation();
  const scope = scopeKeyOf(pathname);
  const previous = useRef(scope);

  useEffect(() => {
    if (previous.current === scope) {
      return;
    }
    previous.current = scope;
    resetDatasetSessionFilters();
  }, [scope]);
};

export const datasetFilterScopeKey = scopeKeyOf;
